import { BellOff, CalendarClock, X } from "lucide-react";
import type { AdminNotification, FollowUpDue } from "@/api/notifications";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { useTicketDock } from "@/components/shared/tickets/ticket-dock-params";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useDeleteNotification,
  useMarkNotificationsRead,
  useNotificationList,
} from "@/hooks/use-notifications";
import { formatRelative, parseIstDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";

/**
 * Text only. The server already sends plain text; this is the second guard, so
 * markup in a message (a ticket title pasted from an email) never shows as tags.
 */
function plainText(value: string): string {
  if (!value.includes("<") && !value.includes("&")) return value;
  const text =
    new DOMParser().parseFromString(value, "text/html").body.textContent ??
    value.replace(/<[^>]*>/g, "");
  return text.replace(/\s+/g, " ").trim();
}

/**
 * The bell's popover body: follow-ups that are due first (they are things to
 * do), then notifications newest first. Keyset "Load more" with `before`.
 */
export function NotificationList({
  canOpenTickets,
  onNavigate,
}: {
  canOpenTickets: boolean;
  onNavigate: () => void;
}) {
  const query = useNotificationList(true);
  const markRead = useMarkNotificationsRead();
  const remove = useDeleteNotification();
  const dock = useTicketDock();

  const pages = query.data?.pages ?? [];
  const items = pages.flatMap((page) => page.items);
  const due = pages[0]?.followUpsDue ?? [];
  const unread = pages[0]?.unread ?? 0;

  const openTicket = (ticketId: number) => {
    dock.open({ kind: "ticket", id: ticketId });
    onNavigate();
  };

  const openNotification = (item: AdminNotification) => {
    if (!item.read) markRead.mutate({ ids: [item.id] });
    if (!canOpenTickets) return;
    if (item.ticketId) openTicket(item.ticketId);
    else if (item.customerId) {
      dock.open({ kind: "customer", id: item.customerId });
      onNavigate();
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
        <h2 className="text-sm font-semibold">Notifications</h2>
        <Button
          variant="ghost"
          size="sm"
          disabled={unread === 0 || markRead.isPending}
          onClick={() => markRead.mutate({ all: true })}
        >
          Mark all read
        </Button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {query.isPending ? (
          <div className="space-y-3 p-3" role="status">
            <span className="sr-only">Loading…</span>
            {Array.from({ length: 4 }, (_, i) => (
              <div key={i} className="space-y-1.5">
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-3 w-full" />
              </div>
            ))}
          </div>
        ) : query.error ? (
          <ErrorState
            title="Couldn't load notifications"
            error={query.error}
            onRetry={() => void query.refetch()}
          />
        ) : (
          <>
            {due.length > 0 && canOpenTickets ? (
              <section aria-label="Follow-ups due">
                <h3 className="px-3 pb-1 pt-3 text-xs font-medium text-muted-foreground">
                  Follow-ups due
                </h3>
                <ul>
                  {due.map((item) => (
                    <li key={item.ticketId}>
                      <FollowUpRow item={item} onOpen={() => openTicket(item.ticketId)} />
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {items.length === 0 && due.length === 0 ? (
              <EmptyState
                icon={BellOff}
                title="Nothing new"
                description="Mentions, hand-overs and follow-ups show up here."
              />
            ) : null}

            {items.length > 0 ? (
              <section aria-label="Notifications">
                {due.length > 0 && canOpenTickets ? (
                  <h3 className="px-3 pb-1 pt-3 text-xs font-medium text-muted-foreground">
                    Earlier
                  </h3>
                ) : null}
                <ul>
                  {items.map((item) => (
                    <li key={item.id}>
                      <NotificationRow
                        item={item}
                        onOpen={() => openNotification(item)}
                        onRemove={() => remove.mutate(item.id)}
                      />
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {query.hasNextPage ? (
              <div className="p-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full"
                  loading={query.isFetchingNextPage}
                  onClick={() => void query.fetchNextPage()}
                >
                  Load more
                </Button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

function FollowUpRow({ item, onOpen }: { item: FollowUpDue; onOpen: () => void }) {
  const at = parseIstDateTime(item.followUpAt);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full items-start gap-2 px-3 py-2 text-left transition-colors hover:bg-muted focus-visible:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
    >
      <CalendarClock
        className="mt-0.5 size-4 shrink-0 text-destructive"
        aria-hidden="true"
      />
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline justify-between gap-2">
          <span className="truncate text-sm font-medium">
            <span className="tabular-nums">{item.number}</span> · {plainText(item.title)}
          </span>
          {at ? (
            <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
              {formatRelative(at)}
            </span>
          ) : null}
        </span>
        <span className="block truncate text-xs text-muted-foreground">
          {item.personName ?? "Follow-up due"}
        </span>
      </span>
    </button>
  );
}

function NotificationRow({
  item,
  onOpen,
  onRemove,
}: {
  item: AdminNotification;
  onOpen: () => void;
  onRemove: () => void;
}) {
  return (
    <div className="group relative flex items-start focus-within:bg-muted hover:bg-muted">
      <button
        type="button"
        onClick={onOpen}
        className="flex min-w-0 flex-1 items-start gap-2 py-2 pl-3 pr-10 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
      >
        <span
          className={cn(
            "mt-1.5 size-2 shrink-0 rounded-full",
            item.read ? "bg-transparent" : "bg-primary",
          )}
          aria-hidden="true"
        />
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline justify-between gap-2">
            <span
              className={cn(
                "truncate text-sm",
                item.read ? "text-muted-foreground" : "font-medium text-foreground",
              )}
            >
              {plainText(item.title)}
            </span>
            <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
              {formatRelative(item.at)}
            </span>
          </span>
          <span className="block truncate text-xs text-muted-foreground">
            {plainText(item.body)}
          </span>
          {item.read ? null : <span className="sr-only">Unread</span>}
        </span>
      </button>
      <Button
        variant="ghost"
        size="icon-sm"
        className="absolute right-1 top-1 text-muted-foreground hover:text-foreground"
        aria-label={`Remove “${plainText(item.title)}”`}
        onClick={onRemove}
      >
        <X aria-hidden="true" />
      </Button>
    </div>
  );
}
